// Contenido editorial del sitio. Vive en el frontend (no toca el backend):
// cada artículo es contenido estático, indexable y editable sin desplegar la API.
//
// Regla SEO aplicada: UNA palabra clave primaria por artículo, sin canibalización
// entre ellos. Cada uno responde una intención de búsqueda distinta.

export const ARTICLES = [
    {
        slug: "que-es-el-papel-grado-alimenticio",
        keyword: "qué es el papel grado alimenticio",
        title: "Qué es el papel grado alimenticio",
        excerpt:
            "No todo el papel puede tocar comida. Te explicamos qué hace que un papel sea grado alimenticio, cómo identificarlo y qué riesgos corres si usas el equivocado.",
        summary:
            "El papel grado alimenticio es papel fabricado con pulpa virgen o pulpa reciclada apta para uso alimentario, sin tintas, adhesivos ni blanqueadores que puedan migrar al alimento. Se diferencia del papel común en que no transfiere sustancias dañinas al estar en contacto directo con comida, especialmente caliente o grasosa.",
        readingMinutes: 6,
        published: "2026-08-13",
        category: "Fundamentos",
        featured: true,
        // Nombres de producto reales, no inventados: se buscan contra el
        // catálogo en vivo al renderizar (ver GuideDetail.js) — si un producto
        // cambia de nombre o se da de baja, la guía deja de mostrarlo en vez
        // de mostrar un enlace roto o un dato viejo.
        relatedProducts: [
            { label: "Para empezar: uso general", names: ["Papel Amarillo", "Papel Canela"] },
        ],
        body: [
            {
                type: "p",
                text: "El papel grado alimenticio es aquel fabricado específicamente para entrar en contacto directo con alimentos sin transferirles sustancias dañinas. No es una categoría comercial ni un adjetivo de marketing: es una especificación técnica sobre de qué está hecho el papel y qué NO contiene.",
            },
            { type: "h2", text: "Qué lo hace diferente del papel común" },
            {
                type: "p",
                text: "La diferencia empieza en la materia prima. El papel grado alimenticio se produce con pulpa virgen o con pulpa reciclada apta para uso alimentario. El papel reciclado común puede contener residuos de tintas, adhesivos, blanqueadores y metales pesados provenientes de su vida anterior como periódico, caja o documento impreso. Esos residuos migran al alimento, especialmente si está caliente o tiene grasa.",
            },
            {
                type: "list",
                items: [
                    "Sin tintas ni colorantes no aptos para contacto con alimentos",
                    "Sin blanqueadores ópticos que puedan migrar al producto",
                    "Resistente a la grasa y a la humedad según su tratamiento (encerado, parafinado, antigrasa)",
                    "Estabilidad a temperatura: no se degrada al contacto con alimentos calientes",
                ],
            },
            { type: "h2", text: "El riesgo real de usar el papel equivocado" },
            {
                type: "p",
                text: "Usar papel no apto no es solo un tema regulatorio. La grasa y el calor son solventes: aceleran la migración de cualquier compuesto presente en el papel hacia la comida. Un taco recién hecho envuelto en papel reciclado común está en el peor escenario posible — caliente, grasoso y en contacto prolongado.",
            },
            {
                type: "p",
                text: "Para un negocio de alimentos, esto se traduce en tres exposiciones concretas: riesgo sanitario para el cliente, riesgo regulatorio en una inspección, y riesgo reputacional si el papel mancha, se rompe o transfiere olor al producto.",
            },
            { type: "h2", text: "Cómo identificar papel realmente apto" },
            {
                type: "p",
                text: "Pregunta siempre a tu proveedor por el origen de la pulpa y el tratamiento aplicado. Un distribuidor serio te lo responde sin rodeos. Desconfía del papel sin especificación de gramaje ni medida: si no te pueden decir cuántos gramos pesa por metro cuadrado, difícilmente te pueden garantizar de qué está hecho.",
            },
            {
                type: "callout",
                text: "Todos los papeles de Carpepel son grado alimenticio. Si necesitas ficha técnica o documentación, pídela por WhatsApp.",
            },
        ],
    },
    {
        slug: "como-elegir-el-gramaje-correcto",
        keyword: "cómo elegir el gramaje del papel",
        title: "Cómo elegir el gramaje del papel",
        excerpt:
            "El gramaje decide si tu papel aguanta o se rompe. Guía práctica para elegir entre 16 y 49 gramos según el tipo de alimento, sin pagar de más ni quedarte corto.",
        summary:
            "El gramaje del papel grado alimenticio se elige según el peso y la grasa del alimento: 16–18 gramos para productos ligeros y secos, 24–30 gramos para uso general (tacos, tortas, antojitos), y 40–49 gramos para producto pesado, húmedo o muy grasoso.",
        readingMinutes: 5,
        published: "2026-08-13",
        category: "Guía práctica",
        featured: false,
        // Agrupado por las mismas tres bandas que el propio artículo explica
        // abajo (16-18g / 24-30g / 40-49g), con el gramaje REAL de cada
        // producto en la base de datos, no una categoría de marketing.
        relatedProducts: [
            { label: "Ligero (16-18g)", names: ["Papel cebolla Verde Pera", "Papel Cebolla Blanco 18g"] },
            { label: "Uso general (24-30g)", names: ["Papel Amarillo", "Papel Oro", "Cafe Tissue 24 gr"] },
            { label: "Alta resistencia (40-49g)", names: ["Papel Revolucion"] },
        ],
        body: [
            {
                type: "p",
                text: "El gramaje es el peso del papel por metro cuadrado, expresado en gramos. Es el número que más impacta en el desempeño real de tu envoltura y, sin embargo, es el que más se ignora al comprar. Un gramaje mal elegido se traduce en papel que se rompe, o en dinero gastado de más en papel más grueso del que necesitas.",
            },
            { type: "h2", text: "La regla básica: grasa y peso mandan" },
            {
                type: "p",
                text: "A mayor contenido de grasa y mayor peso del alimento, mayor gramaje necesitas. Un pan dulce seco no ejerce ninguna exigencia sobre el papel. Unas carnitas escurriendo sí. Antes de elegir gramaje, define esas dos variables de tu producto.",
            },
            { type: "h2", text: "Referencia por rango de gramaje" },
            {
                type: "list",
                items: [
                    "16–18 g — Ligero. Ideal para separar, forrar canastas, envolver pan y productos secos o de poco peso.",
                    "24–30 g — Uso general. El rango más versátil: tacos, tortas, hamburguesas, antojitos. Buen equilibrio entre resistencia y costo.",
                    "40–49 g — Alta resistencia. Para producto pesado, húmedo o muy grasoso, y para uso industrial donde el papel debe soportar manipulación.",
                ],
            },
            { type: "h2", text: "El error más caro" },
            {
                type: "p",
                text: "Comprar siempre el gramaje más alto \"por seguridad\" es el error más común y el más costoso a largo plazo. Si tu producto es ligero y seco, estás pagando un sobreprecio en cada paca sin ninguna ganancia funcional. A la inversa, ahorrar en gramaje con producto grasoso genera desperdicio por ruptura, que sale más caro que el papel correcto.",
            },
            {
                type: "p",
                text: "La prueba definitiva es tu propio producto: pide una muestra del gramaje que estás considerando y úsala en condiciones reales — con el alimento caliente, en su empaque final y con el tiempo de espera que realmente tiene antes de llegar al cliente.",
            },
        ],
    },
    {
        slug: "papel-encerado-vs-estraza",
        keyword: "papel encerado vs estraza",
        title: "Papel encerado vs. estraza: cuál usar",
        excerpt:
            "Se confunden todo el tiempo y no son lo mismo. Diferencias reales en resistencia a la grasa, apariencia y costo, con recomendaciones por tipo de negocio.",
        summary:
            "El papel estraza es kraft sin tratamiento: resistente a la manipulación, pero absorbe grasa y se transparenta con el tiempo. El papel encerado tiene un tratamiento de cera que crea una barrera contra grasa y humedad. Se recomienda estraza para productos secos como pan y tortillas, y encerado para alimentos grasosos como tacos y hamburguesas.",
        readingMinutes: 5,
        published: "2026-08-13",
        category: "Comparativa",
        featured: false,
        // No se etiqueta ningún producto como "encerado" o "estraza": el
        // catálogo no distingue esa propiedad hoy, y afirmarlo sin
        // verificarlo sería un dato falso en una decisión de compra real.
        // Se enlaza por el USO que la propia ficha de cada producto ya
        // declara (tortillas/pan vs. tacos/grasa), que sí es verificable.
        relatedProducts: [
            { label: "Para producto seco (pan, tortillas)", names: ["Papel Cebolla Blanco 18g"] },
            { label: "Para producto con grasa (tacos, antojitos)", names: ["Papel Canela", "Papel domino y de cuadritos"] },
        ],
        body: [
            {
                type: "p",
                text: "Son los dos papeles más pedidos en el sector alimentario mexicano y también los más confundidos entre sí. La diferencia no es de calidad — es de función. Cada uno resuelve un problema distinto.",
            },
            { type: "h2", text: "Papel estraza" },
            {
                type: "p",
                text: "Es papel kraft sin tratamiento de barrera. Su fuerza está en la resistencia mecánica: aguanta manipulación, no se rasga fácilmente y tiene una apariencia natural que muchos negocios buscan deliberadamente por su estética artesanal. Su límite es la grasa: al no tener barrera, la absorbe y se transparenta con el tiempo.",
            },
            { type: "h2", text: "Papel encerado" },
            {
                type: "p",
                text: "Es papel con un tratamiento de cera o parafina que crea una barrera contra la grasa y la humedad. No se transparenta, no deja pasar el aceite y mantiene el producto en mejor estado por más tiempo. Es la opción correcta cuando el alimento escurre.",
            },
            { type: "h2", text: "Cuál elegir" },
            {
                type: "list",
                items: [
                    "Elige estraza si vendes pan, tortillas, productos secos, o si la estética natural del kraft es parte de tu marca.",
                    "Elige encerado si vendes tacos, fritura, hamburguesas, carnitas o cualquier producto con grasa visible.",
                    "Si vendes ambos tipos de producto, tener los dos en inventario sale más barato que forzar uno solo para todo.",
                ],
            },
            {
                type: "callout",
                text: "¿No estás seguro de cuál necesitas? Escríbenos por WhatsApp con lo que vendes y te decimos exactamente qué papel usar.",
            },
        ],
    },
    {
        slug: "cuanto-papel-necesita-tu-negocio",
        keyword: "cuánto papel comprar para mi negocio",
        title: "Cuánto papel necesita tu negocio al mes",
        excerpt:
            "Comprar de más inmoviliza tu dinero; comprar de menos te deja sin producto un sábado. Método simple para calcular tu consumo real y tu punto de reorden.",
        summary:
            "Para calcular el consumo mensual de papel de un negocio de alimentos: multiplica las piezas vendidas en un día promedio por las hojas que usa cada pieza, y ese resultado por los días que abre al mes. El punto de reorden debe ser al menos el consumo del tiempo de entrega del proveedor más un colchón de seguridad, nunca la última paca.",
        readingMinutes: 4,
        published: "2026-08-13",
        category: "Guía práctica",
        featured: false,
        relatedProducts: [
            { label: "Para calcular tu primer pedido", names: ["Papel Amarillo", "Papel Canela"] },
        ],
        body: [
            {
                type: "p",
                text: "La mayoría de los negocios de alimentos compran papel por intuición: \"se está acabando, pide más\". Funciona hasta que se acaba un sábado a mediodía. Calcular el consumo real toma diez minutos y evita tanto el desabasto como el capital inmovilizado en papel guardado.",
            },
            { type: "h2", text: "El cálculo, en tres pasos" },
            {
                type: "list",
                items: [
                    "1. Cuenta cuántas piezas vendes en un día promedio (no en tu mejor día ni en el peor).",
                    "2. Multiplica por las hojas de papel que usa cada pieza. Sé honesto: si envuelves con dos hojas, son dos.",
                    "3. Multiplica por los días que abres al mes. Ese es tu consumo mensual en hojas.",
                ],
            },
            {
                type: "p",
                text: "Con ese número, divide entre las hojas que trae una paca del producto que compras y ya sabes cuántas pacas consumes al mes. Ese es tu piso, no tu pedido: al pedido hay que sumarle margen.",
            },
            { type: "h2", text: "Tu punto de reorden" },
            {
                type: "p",
                text: "El error clásico es pedir cuando ya se acabó. Debes pedir cuando te queda el equivalente a tu tiempo de entrega más un colchón de seguridad. Si tu proveedor tarda tres días en entregarte y consumes una paca cada dos días, tu punto de reorden es de al menos dos pacas restantes — nunca la última.",
            },
            {
                type: "p",
                text: "Considera también la estacionalidad de tu negocio. Diciembre, temporadas escolares y fines de semana largos alteran el consumo promedio de forma predecible. Si sabes que tu venta sube 40% en diciembre, tu pedido de noviembre debe reflejarlo.",
            },
        ],
    },
    {
        slug: "normativa-papel-grado-alimenticio-mexico",
        keyword: "normativa papel grado alimenticio méxico",
        title: "Normativa del papel grado alimenticio en México",
        excerpt:
            "Se asume que hay una NOM específica para el papel, como la hay para los plásticos. No es así. Esto es lo que realmente aplica y qué pedirle a tu proveedor.",
        summary:
            "En México la NOM-251-SSA1-2009 exige que cualquier material en contacto directo con alimentos sea no tóxico, pero no existe una NOM dedicada a la composición del papel grado alimenticio como sí existe para los plásticos (migración de metales pesados y monómeros residuales). La conformidad depende de la higiene general del negocio y de la documentación que te entregue tu proveedor sobre el origen y tratamiento del papel.",
        readingMinutes: 5,
        published: "2026-08-31",
        category: "Normativa",
        featured: false,
        relatedProducts: [
            { label: "Papel grado alimenticio de Carpepel", names: ["Papel Oro", "Papel Amarillo"] },
        ],
        body: [
            {
                type: "p",
                text: "Es una pregunta que nos hacen seguido: \"¿qué NOM certifica que este papel es grado alimenticio?\". La respuesta honesta sorprende a la mayoría: no existe una Norma Oficial Mexicana dedicada específicamente a la composición del papel para contacto con alimentos, como sí existe para los plásticos. Eso no significa que el papel esté fuera de toda regulación — significa que la conformidad se demuestra distinto.",
            },
            { type: "h2", text: "La norma que sí aplica: NOM-251-SSA1-2009" },
            {
                type: "p",
                text: "La NOM-251-SSA1-2009, \"Prácticas de higiene para el proceso de alimentos, bebidas o suplementos alimenticios\", emitida por la Secretaría de Salud, es la que rige en la práctica. Exige que cualquier material en contacto directo con el producto — incluido el envase primario, la categoría en la que cae el papel que envuelve o empaca comida — sea no tóxico y no comprometa la inocuidad del alimento.",
            },
            {
                type: "p",
                text: "Es una norma de prácticas de higiene para el proceso, no una especificación técnica de materiales. Cubre cómo debe operar un negocio de alimentos, y dentro de eso, exige que lo que toca la comida sea seguro — pero no define gramajes, tratamientos ni composiciones específicas del papel.",
            },
            { type: "h2", text: "Lo que NO existe: una NOM de material para papel" },
            {
                type: "p",
                text: "Para plásticos en contacto con alimentos sí hay especificaciones técnicas más concretas, centradas en límites de migración de metales pesados y monómeros residuales. Para papel, esa norma de material equivalente no existe en el marco regulatorio mexicano actual. Esto significa que \"grado alimenticio\" aplicado a papel es una descripción respaldada por el origen de la pulpa, el tratamiento aplicado y la documentación del fabricante — no un número de certificado único que puedas verificar en una base de datos.",
            },
            { type: "h2", text: "Qué pedir a tu proveedor, ya que no hay un solo sello que lo resuelva" },
            {
                type: "list",
                items: [
                    "Origen de la pulpa: virgen o reciclada certificada para uso alimentario, nunca reciclado común de origen no controlado.",
                    "Tratamiento aplicado y para qué está diseñado (encerado, parafinado, antigrasa) — debe poder explicártelo sin rodeos.",
                    "Ficha o especificación del producto: gramaje y medida exactos, no aproximados.",
                    "Trazabilidad del proveedor: que sea una empresa identificable, con historial, no un vendedor sin nombre comercial verificable.",
                ],
            },
            {
                type: "callout",
                text: "Carpepel es proveedor oficial de Austin Star Paper. Si tu negocio necesita documentación para una inspección o auditoría, pregúntanos directamente por WhatsApp.",
            },
            { type: "h2", text: "Fuentes" },
            {
                type: "p",
                text: "NOM-251-SSA1-2009, Prácticas de higiene para el proceso de alimentos, bebidas o suplementos alimenticios — Diario Oficial de la Federación (dof.gob.mx). Este artículo es informativo y no constituye asesoría legal ni regulatoria; para el cumplimiento específico de tu negocio, consulta a COFEPRIS o a un asesor especializado en regulación sanitaria.",
            },
        ],
    },
    {
        slug: "papel-antigrasa-restaurantes",
        keyword: "papel antigrasa para restaurantes",
        title: "Papel antigrasa para restaurantes: cómo elegirlo",
        excerpt:
            "No todo el papel resiste la grasa igual. Qué buscar si tu negocio maneja fritura, carnes o comida grasosa, y qué pasa si eliges el papel equivocado.",
        summary:
            "El papel antigrasa para restaurantes es papel con un tratamiento (encerado, parafinado o similar) que crea una barrera contra el aceite y la humedad, evitando que se transparente, se rompa o transfiera olor a la comida. Se recomienda para tacos, frituras, carnes y cualquier producto con grasa visible; el papel sin tratamiento (como el estraza común) no es apto para este uso.",
        readingMinutes: 5,
        published: "2026-09-04",
        category: "Guía práctica",
        featured: false,
        // Canela ya se recomienda para grasa en la guía encerado-vs-estraza —
        // es el único producto vivo en el catálogo con esa función declarada.
        // No se etiqueta como "100% a prueba de grasa" porque esa
        // especificación no está verificada.
        relatedProducts: [
            { label: "Con tratamiento antigrasa", names: ["Papel Canela"] },
        ],
        body: [
            {
                type: "p",
                text: "Un restaurante que maneja fritura, carnes o cualquier producto grasoso tiene un problema distinto al de una panadería: el papel no solo tiene que envolver, tiene que aguantar el aceite sin transparentarse, romperse ni pasar el olor a la comida. Ahí es donde entra el papel antigrasa.",
            },
            { type: "h2", text: "Qué hace que un papel sea antigrasa" },
            {
                type: "p",
                text: "No es una propiedad del papel base — es un tratamiento que se le aplica. El encerado y el parafinado son los más comunes: crean una barrera que impide que el aceite penetre la fibra del papel. Sin ese tratamiento, cualquier papel eventualmente absorbe la grasa y se vuelve translúcido, además de perder resistencia.",
            },
            { type: "h2", text: "Señales de que tu papel actual no es el correcto" },
            {
                type: "list",
                items: [
                    "Se transparenta o se ve \"aceitoso\" poco después de envolver el producto.",
                    "Se rompe o se rasga con la manipulación normal, no solo con uso forzado.",
                    "Transfiere olor o sabor entre distintos productos envueltos con el mismo papel.",
                    "El cliente recibe el producto con el papel ya manchado o débil al momento de la entrega.",
                ],
            },
            { type: "h2", text: "Qué preguntar antes de comprar" },
            {
                type: "p",
                text: "Pide a tu proveedor que te diga explícitamente si el papel tiene tratamiento antigrasa (y cuál) o si es papel sin tratamiento. Un papel estraza común, por ejemplo, no está diseñado para grasa visible aunque muchos negocios lo usan por costumbre — funciona para pan y tortillas, no para fritura.",
            },
            {
                type: "callout",
                text: "¿No sabes si tu papel actual tiene tratamiento antigrasa? Escríbenos por WhatsApp con tu producto y te confirmamos qué opción del catálogo te conviene.",
            },
        ],
    },
    {
        slug: "empaques-sostenibles-negocios-comida",
        keyword: "empaques sostenibles para negocios de comida",
        title: "Empaques sostenibles: qué significa realmente",
        excerpt:
            "\"Sostenible\" se usa mucho y se explica poco. Qué factores determinan si un empaque lo es de verdad, y qué preguntar antes de cambiar tu papel o bolsas.",
        summary:
            "Un empaque es más sostenible cuando reduce residuos reales: el material es reciclable o de origen renovable, el tamaño está bien dimensionado para el producto (sin sobre-empacar), y el negocio evita comprar de más por miedo a quedarse corto. No es una etiqueta que un producto tenga o no tenga — es una combinación de material, uso correcto y consumo responsable.",
        readingMinutes: 5,
        published: "2026-09-04",
        category: "Fundamentos",
        featured: false,
        // Guía intencionalmente genérica: no se afirma que ningún producto
        // de Carpepel sea biodegradable, compostable o esté certificado como
        // sostenible — esa verificación no existe hoy. Se enlaza kraft/estraza
        // porque es un hecho verificable de la categoría (papel sin
        // recubrimiento plástico), no una certificación del proveedor.
        relatedProducts: [
            { label: "Papel base, sin recubrimiento plástico", names: ["Papel Cebolla Blanco 18g"] },
        ],
        body: [
            {
                type: "p",
                text: "\"Empaque sostenible\" es una de las frases más usadas y menos explicadas en el sector de alimentos. No es una certificación única ni una etiqueta que un producto tiene o no tiene — depende del material, de cómo se usa y de cuánto se desperdicia.",
            },
            { type: "h2", text: "Los tres factores que realmente importan" },
            {
                type: "list",
                items: [
                    "Material: el papel sin recubrimiento plástico es, como categoría, más fácil de reciclar que los laminados plástico-papel — pero verifica siempre con tu proveedor de reciclaje local qué acepta realmente.",
                    "Dimensionamiento correcto: un empaque más grande de lo necesario genera más residuo por pieza vendida, sin importar de qué material esté hecho.",
                    "Consumo responsable: comprar el volumen correcto (ver nuestra guía de cuánto papel necesita tu negocio) evita que producto en buen estado se deseche por mal manejo de inventario.",
                ],
            },
            { type: "h2", text: "Lo que hay que evitar: el \"greenwashing\" accidental" },
            {
                type: "p",
                text: "Es fácil, sin mala intención, terminar afirmando algo que no se puede respaldar — \"biodegradable\", \"compostable\" o \"ecológico\" son términos con implicaciones técnicas específicas. Si tu proveedor no te puede mostrar de dónde sale esa afirmación, es más honesto (y más seguro para tu negocio) describir el producto por lo que sí es verificable: de qué está hecho y qué tratamiento tiene.",
            },
            {
                type: "p",
                text: "Esto también te protege a ti como negocio: un cliente o inspector puede pedirte que respaldes cualquier afirmación ambiental que hagas en tu propio empaque o publicidad.",
            },
            {
                type: "callout",
                text: "¿Buscas reducir el papel que desperdicias sin cambiar de proveedor? Escríbenos por WhatsApp y revisamos contigo el gramaje y tamaño que realmente necesitas.",
            },
        ],
    },
    {
        slug: "papel-cebolla-que-es-y-para-que-sirve",
        keyword: "papel cebolla para tortillas",
        title: "Papel cebolla: qué es y para qué sirve",
        excerpt:
            "Se llama así por lo delgado que es, no por ningún ingrediente. Qué es el papel cebolla, para qué se usa y qué gramaje conviene según tu negocio.",
        summary:
            "El papel cebolla es papel grado alimenticio de bajo gramaje (16 a 24g), delgado y ligeramente translúcido — el nombre viene de su parecido con la piel de una cebolla, no de ningún ingrediente. Se usa para envolver tortillas y productos secos porque retiene el calor sin volverse pesado ni pegarse al producto. A menor gramaje (16-18g) es más ligero y económico por hoja; a mayor gramaje (24g) aguanta más manipulación sin romperse.",
        readingMinutes: 5,
        published: "2026-09-10",
        category: "Guía práctica",
        featured: false,
        relatedProducts: [
            { label: "Ligero, para tortillas y producto seco", names: ["Papel cebolla Verde Pera", "Papel Cebolla Blanco 18g"] },
            { label: "Más cuerpo, para manipulación frecuente", names: ["Papel Cebolla Blanco 24g"] },
        ],
        body: [
            {
                type: "p",
                text: "\"Papel cebolla\" es el nombre con el que se conoce en el ramo alimenticio a un papel grado alimenticio delgado y ligeramente translúcido — el nombre describe su apariencia, parecida a la piel seca de una cebolla, no un ingrediente ni un tratamiento especial. Es, junto con el estraza, el papel que más se ve envolviendo tortillas en México.",
            },
            { type: "h2", text: "Por qué se usa tanto para tortillas" },
            {
                type: "p",
                text: "Un papel grueso retiene más calor pero pesa más y sube el costo por pieza envuelta; un papel demasiado ligero se rompe con la manipulación normal de un negocio de alto volumen. El papel cebolla vive en el punto medio: suficiente cuerpo para sostener tortillas calientes sin romperse, suficientemente ligero para no encarecer cada envoltura.",
            },
            { type: "h2", text: "Qué gramaje elegir según tu negocio" },
            {
                type: "list",
                items: [
                    "16 a 18g: el más común para tortillerías — producto ligero, seco, rotación alta. Es el gramaje más económico por hoja.",
                    "24g: cuando el papel se manipula más (se abre y cierra varias veces, o envuelve pedidos para llevar que viajan) y necesita más resistencia sin pasar a un papel de uso general.",
                    "Si además envuelves producto con grasa visible (tacos, frituras), el papel cebolla no es la opción — revisa nuestra guía de papel antigrasa para restaurantes.",
                ],
            },
            { type: "h2", text: "Blanco o de color: solo es presentación" },
            {
                type: "p",
                text: "La diferencia entre un papel cebolla blanco y uno de color (como el verde pera) es únicamente cosmética — mismo gramaje, mismo uso, misma función. Elige por la presentación que quieras dar en mostrador, no porque un color \"aguante más\" que otro: no es así.",
            },
            {
                type: "callout",
                text: "¿No sabes si tu negocio necesita 16-18g o 24g? Escríbenos por WhatsApp con tu volumen y tipo de producto y te confirmamos el gramaje correcto.",
            },
        ],
    },
    {
        slug: "papel-para-tortillas-al-mayoreo",
        keyword: "guía de compra papel para tortillas al mayoreo",
        title: "Papel para tortillas al mayoreo: guía de compra",
        excerpt:
            "Antes de comprar por volumen: gramaje, tipo de papel y qué preguntar a tu proveedor para no llevarte una sorpresa en el lote.",
        summary:
            "Para comprar papel para tortillas al mayoreo, lo primero es fijar el gramaje según el uso (16-18g para tortillerías de rotación alta, hasta 29g si el papel también carga producto más pesado) y el tipo de papel (cebolla o estraza para tortillas secas; con tratamiento antigrasa si también envuelves fritura). Al comprar por volumen, verifica que el gramaje y la medida sean consistentes lote a lote — es lo que más varía entre proveedores.",
        readingMinutes: 6,
        published: "2026-09-10",
        category: "Guía práctica",
        featured: false,
        relatedProducts: [
            { label: "Para tortillerías de rotación alta", names: ["Papel cebolla Verde Pera", "Papel Cebolla Blanco 18g"] },
            { label: "Uso general, más cuerpo", names: ["Papel Amarillo", "Papel Oro"] },
        ],
        body: [
            {
                type: "p",
                text: "Comprar papel para tortillas al mayoreo cambia de problema respecto a comprar unas cuantas pacas: un error de gramaje o de medida no te cuesta una compra, te cuesta un lote completo. Antes de cotizar por volumen, hay tres decisiones que conviene cerrar primero.",
            },
            { type: "h2", text: "1. El gramaje según tu volumen y tipo de producto" },
            {
                type: "p",
                text: "Para tortillerías con rotación alta de producto seco, 16 a 18g (papel cebolla) es lo más usado — es el más económico por hoja y suficiente para el tiempo que la tortilla pasa envuelta. Si tu negocio también envuelve producto más pesado con el mismo papel (tortas, antojitos), 24 a 29g te da más margen sin cambiar de proveedor por cada línea de producto.",
            },
            { type: "h2", text: "2. Cebolla o estraza: no son intercambiables por costumbre" },
            {
                type: "p",
                text: "El papel cebolla es más ligero y ligeramente translúcido; el estraza (kraft sin tratamiento) es más opaco y con más cuerpo al mismo gramaje. Ambos sirven para tortillas secas — la diferencia real aparece si tu negocio también maneja algo de grasa: ahí ninguno de los dos es la opción correcta y conviene un papel con tratamiento antigrasa (ver nuestra guía de papel encerado vs. estraza).",
            },
            { type: "h2", text: "3. Qué preguntar al proveedor ANTES de cerrar el volumen" },
            {
                type: "list",
                items: [
                    "¿El gramaje es consistente lote a lote, o varía con la producción? Un gramaje que se mueve ±2g cambia cuánto papel rinde por kilo.",
                    "¿La medida es la nominal o la real? Un papel \"34×45\" que en realidad mide 33×44 no se nota en una hoja, sí en una paca completa.",
                    "¿Cómo se empaqueta — por paca (peso fijo) o por hoja contada? Cambia cómo calculas tu costo por unidad envuelta.",
                    "¿Hay mínimo de compra por mayoreo y a partir de qué volumen mejora el precio?",
                ],
            },
            {
                type: "callout",
                text: "Cotiza tu volumen exacto por WhatsApp: te confirmamos gramaje, medida y precio por mayoreo sin mínimos escondidos en la conversación.",
            },
        ],
    },
    {
        slug: "bolsas-de-poliseda-que-son-y-para-que-sirven",
        keyword: "bolsas de poliseda",
        title: "Bolsas de poliseda: qué son y para qué se usan",
        excerpt:
            "La poliseda no es un tipo de papel, es un plástico distinto al de la bolsa común. Qué es, por qué se usa tanto en panaderías y cómo se compra al mayoreo.",
        summary:
            "La poliseda es un plástico (polipropileno biorientado) de acabado brillante que recuerda a la seda, de ahí el nombre. A diferencia de la bolsa de plástico común (más opaca y flexible), la poliseda es más rígida y transparente, por lo que se usa para dar presentación a pan, tortillas y productos de mostrador. Se vende al mayoreo por kilogramo, no por pieza, porque el peso varía según el tamaño y calibre de la bolsa.",
        readingMinutes: 4,
        published: "2026-09-10",
        category: "Fundamentos",
        featured: false,
        relatedProducts: [
            { label: "Bolsa de poliseda al mayoreo", names: ["Rollo de Poliseda GreenBag"] },
        ],
        body: [
            {
                type: "p",
                text: "La poliseda no es un tipo de papel, aunque conviva en el mismo pasillo del catálogo que el papel grado alimenticio — es un plástico (polipropileno biorientado, conocido también como BOPP) con un acabado brillante que recuerda a la seda. De ahí el nombre.",
            },
            { type: "h2", text: "En qué se diferencia de una bolsa de plástico común" },
            {
                type: "p",
                text: "Una bolsa de plástico común (tipo camiseta o de alta densidad) es más flexible y opaca, pensada para cargar. La poliseda es más rígida y transparente — se dobla menos, pero deja ver el producto con claridad. Por eso es la elegida cuando el empaque también es parte de la presentación: pan de dulce, tortillas de harina, productos de mostrador.",
            },
            { type: "h2", text: "Por qué se usa en panaderías y tortillerías" },
            {
                type: "list",
                items: [
                    "Transparencia real: el cliente ve el producto sin abrir la bolsa.",
                    "Mayor rigidez que el plástico común: sostiene mejor la forma del producto en mostrador.",
                    "No absorbe humedad ni transfiere olor, a diferencia de una bolsa de papel para el mismo uso.",
                ],
            },
            { type: "h2", text: "Cómo se compra al mayoreo" },
            {
                type: "p",
                text: "A diferencia del papel, que se compra por paca de hojas, la poliseda se vende por kilogramo — el peso de una bolsa cambia según su tamaño y calibre, así que cobrar por pieza no sería consistente entre presentaciones. Al cotizar, especifica la medida que necesitas (las más comunes van de 20×30 a 30×40 cm) para calcular cuántas bolsas rinde cada kilo.",
            },
            {
                type: "callout",
                text: "¿No sabes qué medida de poliseda te conviene? Escríbenos por WhatsApp con el tamaño de tu producto y te ayudamos a calcular el rendimiento por kilo.",
            },
        ],
    },
];

export const getArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
export const getFeatured = () => ARTICLES.find((a) => a.featured) || ARTICLES[0];
export const getRest = () => ARTICLES.filter((a) => !a.featured);
